import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import styles from './option.module.css';

type OptionCssVar = '--ui-option-padding';

type SlotAlignment = 'start' | 'center' | 'end';

const ALIGNMENT_STYLES = {
  start: styles.alignStart ?? '',
  center: styles.alignCenter ?? '',
  end: styles.alignEnd ?? '',
} satisfies Record<SlotAlignment, string>;

interface OptionProps extends HTMLAttributes<HTMLDivElement> {
  startSlot?: ReactNode;
  startSlotAlignment?: SlotAlignment;
  endSlot?: ReactNode;
  endSlotAlignment?: SlotAlignment;
  contentAlignment?: SlotAlignment;
  isSelected?: boolean;
  disabled?: boolean;
  cssVars?: Partial<Record<OptionCssVar, string>>;
}

export const Option = ({
  startSlot,
  startSlotAlignment = 'center',
  endSlot,
  endSlotAlignment = 'center',
  contentAlignment = 'center',
  children,
  className,
  isSelected,
  disabled,
  onClick,
  cssVars,
  style,
  ...props
}: OptionProps) => {
  return (
    <div
      aria-selected={isSelected ?? false}
      className={classNames([
        className,
        styles.option,
        isSelected && styles.selected,
        disabled && styles.disabled,
      ])}
      aria-disabled={disabled || undefined}
      style={cssVars ? { ...cssVars, ...style } : style}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {startSlot && (
        <div
          className={classNames(
            styles.startSlot,
            ALIGNMENT_STYLES[startSlotAlignment]
          )}
        >
          {startSlot}
        </div>
      )}
      <div
        className={classNames(
          styles.content,
          ALIGNMENT_STYLES[contentAlignment]
        )}
      >
        {children}
      </div>
      {endSlot && (
        <div
          className={classNames(
            styles.endSlot,
            ALIGNMENT_STYLES[endSlotAlignment]
          )}
        >
          {endSlot}
        </div>
      )}
    </div>
  );
};
